export const STAGES = ['0-10', '10-20', '20-30', '30-40', '40-50', '51+'] as const;
export type AgeStage = typeof STAGES[number];
export interface Card {
  id: string;
  stage: AgeStage | 'all';
  titleZh: string;
  titleEn: string;
  descriptionZh: string;
  descriptionEn: string;
}
export interface GameDecks { life: Card[]; setbacks: Card[]; questions: Card[] }
export type RoomPhase = 'lobby' | 'initial-selection' | 'setback' | 'questions' | 'summary';
export type Decision = 'accept' | 'reject';
export interface SelectionHistoryEntry {
  id: string;
  type: 'initial-selection' | 'test-discard';
  at: string;
  cardIds: string[];
}
export interface SetbackHistoryEntry {
  id: string;
  type: 'setback';
  at: string;
  stage: AgeStage;
  setbackId: string;
  decision: Decision;
  discardedCardIds: string[];
}
export interface QuestionHistoryEntry {
  id: string;
  type: 'questions';
  at: string;
  stage: AgeStage;
  questionIds: string[];
}
export type HistoryEntry = SelectionHistoryEntry | SetbackHistoryEntry | QuestionHistoryEntry;
export const DRAW_DURATION_MS = 1500;
export interface DrawRecord { requestId: string; startedAt: number; revealAt: number }
export type TurnState = 'deck' | 'deciding' | 'discussion' | 'drawQuestion1' | 'drawQuestion2' | 'drawQuestion3' | 'nextAgeStage';
export interface RoomTurn { playerId: string | null; playerIndex: number; state: TurnState }
export interface DrawVisual { kind:'setback'|'questions'; phase:'opening'|'choosing'|'selected'; count:number; slot:number|null; sequence:number; questionIndex?:number }
export interface TurnPreview { draw:DrawVisual|null; decision:Decision|null }
export interface StageRecord {
  stage: AgeStage;
  setbackId: string;
  decision: Decision | null;
  discardedCardIds: string[];
  questionIds: string[];
  questionsCompleted: boolean;
  setbackDraw: DrawRecord | null;
  questionDraw: DrawRecord | null;
  questionDraws?: DrawRecord[];
  questionRedraws?: {requestId:string;at:string;questionIds:string[];drawRequestIds:string[];indices?:number[]}[];
  setbackCompleted?: boolean;
  endTurn?: { requestId:string; actorId:string; at:string };
}
export interface Player {
  id: string;
  nickname: string;
  candidateCards: string[];
  initialCards: string[];
  currentCards: string[];
  discardedCards: string[];
  initialConfirmed: boolean;
  acceptedSetbacks: string[];
  stages: StageRecord[];
  lifeHistory: HistoryEntry[];
}
export interface PrivatePlayer extends Player { tokenHash: string }
export interface RoomState {
  schemaVersion: 3;
  code: string;
  hostId: string;
  phase: RoomPhase;
  stageIndex: number;
  revision: number;
  createdAt: string;
  transitions: { id: string; stage: AgeStage; at: string }[];
  catalogue: GameDecks | null;
  turn?: RoomTurn | null;
  players: PrivatePlayer[];
}
export interface PreviousRoomState extends Omit<RoomState, 'schemaVersion' | 'players'> {
  schemaVersion: 2;
  players: (Omit<PrivatePlayer, 'stages'> & { stages: Omit<StageRecord, 'setbackDraw' | 'questionDraw'>[] })[];
}
export interface LegacyRoomState extends Omit<RoomState, 'schemaVersion' | 'phase' | 'stageIndex' | 'transitions' | 'catalogue' | 'players'> {
  schemaVersion: 1;
  phase: 'lobby' | 'initial-selection' | 'sync-test';
  players: Omit<PrivatePlayer, 'stages' | 'acceptedSetbacks'>[];
}
export interface PublicPlayer extends Player {
  connected: boolean;
  previewSelection: string[];
}
export interface RoomSnapshot {
  reviews?:Record<string,LifeReview>;
  reviewEnabled?:boolean;
  code: string;
  hostId: string;
  phase: RoomPhase;
  stageIndex: number;
  revision: number;
  turnPlayerId: string | null;
  turn: RoomTurn | null;
  turnPreview: TurnPreview;
  drawPool: { setbacks:number; questions:number } | null;
  serverNow: number;
  players: PublicPlayer[];
}
export interface Identity { roomCode: string; playerId: string; token: string }
export interface AgeTransitionEvent {
  id:string;
  roomCode:string;
  fromStageIndex:number;
  toStageIndex:number;
  revision:number;
  startedAt:number;
}
export type ReviewLanguage = 'zh-CN' | 'en';
export interface ReviewSections { title:string; life:string; card:string; observation:string }
export interface LifeReview {
  promptVersion?:2;
  language:ReviewLanguage;
  status:'pending'|'ready';
  source:'ai'|'template';
  startedAt:number;
  sections?:ReviewSections;
  reason?:'disabled'|'daily-limit'|'unavailable';
}
export type ClientMessage =
  | { type:'kick-player'; playerId:string; expectedRevision:number }
  | { type:'reorder-players'; playerIds:string[]; expectedRevision:number }
  | { type:'request-review'; playerId:string; language:ReviewLanguage }
  | { type: 'auth'; playerId: string; token: string }
  | { type: 'start' }
  | { type: 'confirm-initial'; cardIds: string[] }
  | { type: 'preview'; cardIds: string[] }
  | { type: 'cancel-preview' }
  | { type: 'draw-visual'; visual:DrawVisual|null }
  | { type: 'preview-decision'; decision:Decision|null }
  | { type: 'draw-setback' | 'draw-questions'; requestId: string; expectedRevision: number; questionIndex?: number }
  | { type: 'confirm-setback'; requestId: string; expectedRevision: number; decision: Decision; cardIds: string[] }
  | { type: 'complete-questions'; requestId: string; expectedRevision: number }
  | { type: 'redraw-questions'; requestId:string; expectedRevision:number; indices?:number[] }
  | { type: 'end-turn'; requestId:string; expectedRevision:number }
  | { type: 'next-stage'; requestId: string; expectedRevision: number };
export type ServerMessage =
  | { type:'age-transition'; transition:AgeTransitionEvent }
  | { type: 'snapshot'; room: RoomSnapshot; cards: Card[]; environment: string }
  | { type: 'error'; code: string; messageZh: string; messageEn: string }
  | { type: 'ack'; requestId: string; revision: number };
